/**
 * Follow-up „Was sagt der OGH dazu?“ nach verifizierten LEX-Treffern.
 * Feuert denselben JUD-HTTP `/v1/jud/answer` (top_k=5), kein zweiter Endpoint.
 * lexCarry = nur Stempel + norm_id/title — kein fragment_text, keine PII.
 *
 * Produktvertrag 26.08.2026: hartes Join, Auto+Chip derselbe HTTP-Pfad.
 * Auto: keine Fake-User-Blase; eine ARKO-Stimme + ausklappbare Originale.
 * Chip-Klick: sichtbare User-Aktion. Judikatur-Suchfeld bleibt unabhängige Anwaltssuche.
 */

import type { LexProof, LexProofSource } from './lexProof';
import { isVerifiedLexProof } from './lexProof';

export {
  JUD_HARD_JOIN_EMPTY_TEXT,
  JUD_TOPIC_JOIN_EMPTY_TEXT,
  JOIN_SYNTHESIS_CITE_MAX,
  OGH_FOLLOWUP_ACTION,
  PORTAL_JUD_SEARCH_FIELD_MARKER,
  PORTAL_JUD_SEARCH_LAWYER_MARKER,
  PORTAL_LEX_JUD_JOIN_HEADNOTE_MARKER,
  PORTAL_LEX_JUD_JOIN_FRAGEPUNKT_MARKER,
  PORTAL_LEX_JUD_JOIN_MARKER,
  PORTAL_LEX_JUD_JOIN_NO_GHOST_MARKER,
  PORTAL_LEX_JUD_JOIN_TOPIC_MARKER,
  PORTAL_LEX_JUD_JOIN_VOICE_MARKER,
  applyJoinTopicToJudProof,
  buildLexJudJoinSynthesis,
  shouldRenderLexQuoteAsLead,
  canonNormId,
  caseHeadnoteMatchesLexIds,
  extractHeadnoteNormIds,
  filterCasesByJoinTopic,
  filterCasesByLexCarry,
  hasClearLegalQuestion,
  headnoteMentionsNorm,
  isComposerParagraphLookup,
  isPremiumPortalCaller,
  lexCarryIntersectsNormRefs,
  shouldAutoOghJoin,
  shouldShowOghFollowupUserBubble,
  type AutoOghJoinInput,
  type JoinTopicFilterResult,
  type JoinTopicInput,
  type OghFollowupOrigin,
} from './portalLexJudJoin';

export const OGH_FOLLOWUP_CHIP_LABEL = 'Was sagt der OGH dazu?';

export interface LexCarrySource {
  norm_id: string;
  title?: string;
}

export interface LexCarry {
  lex_stamp?: string;
  snapshot_version?: string;
  sources: LexCarrySource[];
}

export function lexCarryNormIds(carry: LexCarry | null | undefined): string[] {
  if (!carry?.sources?.length) return [];
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const s of carry.sources) {
    const id = String(s.norm_id || '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
    if (ids.length >= 8) break;
  }
  return ids;
}

export function sanitizeLexCarry(proof: LexProof | null | undefined): LexCarry | null {
  if (!proof || !isVerifiedLexProof(proof)) return null;
  const sources: LexCarrySource[] = [];
  const seen = new Set<string>();
  for (const raw of proof.sources ?? []) {
    const src = sanitizeCarrySource(raw);
    if (!src || seen.has(src.norm_id)) continue;
    seen.add(src.norm_id);
    sources.push(src);
    if (sources.length >= 8) break;
  }
  const stamp = String(proof.lex_stamp || '').trim().slice(0, 120);
  if (!stamp && sources.length === 0) return null;
  const snapshot = String(proof.snapshot_version || '').trim().slice(0, 96);
  return {
    ...(stamp ? { lex_stamp: stamp } : {}),
    ...(snapshot ? { snapshot_version: snapshot } : {}),
    sources,
  };
}

function sanitizeCarrySource(raw: LexProofSource): LexCarrySource | null {
  const normId = String(raw.norm_id || '').trim().slice(0, 64);
  if (!normId) return null;
  const title = String(raw.title || '').replace(/\s+/g, ' ').trim().slice(0, 160);
  return title ? { norm_id: normId, title } : { norm_id: normId };
}

/** Topic + Norm-IDs → JUD-Query. Leer ohne Thema und ohne Normen = kein Call. */
export function buildOghFollowupPrompt(topic: string, normIds: string[]): string | null {
  const t = topic.replace(/\s+/g, ' ').trim().slice(0, 500);
  const ids = normIds.map((id) => id.trim()).filter(Boolean).slice(0, 8);
  if (!t && ids.length === 0) return null;
  const parts = ['Was sagt der OGH dazu'];
  if (t) parts.push(t);
  if (ids.length) parts.push(`Normen: ${ids.join(', ')}`);
  return parts.join('. ').slice(0, 1000);
}
